import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  alpha,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DayCalendarSkeleton,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import * as _ from "lodash";
import moment from "moment-timezone";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { useLastLocation } from "react-router-dom-last-location";
import {
  getVendorCalendlySlots,
  submitAmbassadorSelectedSlot,
  submitAmbassadorSuggestedSlot,
} from "../../../api/invitations";
import { getBlockedDates } from "../../../api/projects";
import {
  AppAlert,
  AppAutoSelect,
  DefaultButton,
  PrimaryButton,
  RequestCoordinationButton,
} from "../../../components/form";
import { useHolidayDateKeys } from "../../../hooks/useHolidayDateKeys";
import { FormHelperTextError } from "../../../components/form/errors";
import { SlotSelectionModal } from "../../../components/invitations";
import { BrowserTimezoneAlert } from "../../../components/BrowserTimezoneAlert";
import FlexBox from "../../../components/layouts/Flexbox";
import { LoadingSpinner } from "../../../components/loading-spinners";
import { APP_COLORS, ENUM_PROJECT_STATUS } from "../../../enums";
import { ROUTE_PATHS } from "../../../routes/paths";
import {
  getFromAndToDateTimeRange,
  getStartAndEndOfMonth,
  isSameDate,
} from "../../../services/utils/date-and-time.utils";
import { useAppDispatch, useAppSelector } from "../../../store";
import { checkAndGetInvitationStatus } from "../../my-projects/utils";
import {
  getUserAvailableTimeSlots,
} from "../utils";
import { SlotModalState, VendorSlot } from "../vendor-slot-selection/types";
import { AmbassadorManualSlotsSection } from "./AmbassadorManualSlotsSection";
import { DateWiseSlotPicker } from "./DateWiseSlotPicker";
import { buildSuggestedTimeSlotsPayload, groupAvailableTimesByDate } from "./utils";

export default function AmbassadorSlotSelection({
  onCancel,
  availableDates,
  invitationId,
  value,
  timezone,
  isCalendly = false,
  projectDetails,
  onSuccess,
}) {
  const { lastLocation } = useLastLocation();
  const navigate = useNavigate();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data, loading: timezoneLoading } = useAppSelector(
    (state) => state.lists.timezones
  );
  const [customSlotState, setCustomSlotState] = useState<{
    slots: VendorSlot[];
  }>({
    slots: [],
  });
  const [customSlotModalState, setCustomSlotModalState] =
    useState<SlotModalState>(null);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [manualFormError, setManualFormError] = useState("");
  const [manualSubmitClicked, setManualSubmitClicked] = useState(false);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const selectedTimezoneVal =
    timezone ||
    data?.find((t) => t?.utc?.find((tutc) => tutc === browserTimezone))?.value;

  const [availableDatesArr, setAvailableDatesArr] = useState(availableDates);
  const [selectedSlot, setSelectedSlot] = useState(value);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
  const [isTimeConfirmed, setIsTimeConfirmed] = useState(!!value);
  const [selectedTimezone, setSelectedTimezone] = useState(selectedTimezoneVal);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [loadingBlockedDates, setLoadingBlockedDates] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    getStartAndEndOfMonth({
      date: value ? new Date(value) : new Date(),
      config: {
        format: "MM-DD-YYYY",
        minDate: new Date(),
      },
    })
  );
  const holidayYear = useMemo(() => {
    if (!selectedMonth?.startOfMonth) return dayjs().year();
    return moment(selectedMonth.startOfMonth, "MM-DD-YYYY").year();
  }, [selectedMonth]);
  const holidayDates = useHolidayDateKeys(isCalendly ? holidayYear : null);
  const [topSectionCollapsed, setTopSectionCollapsed] = useState(false);
  const [noCalendlySlotsFound, setNoCalendlySlotsFound] = useState(false);
  const calendlyInitialScanDoneRef = useRef(false);
  const skipNextCalendlyMonthFetchRef = useRef(false);
  const prevCalendlyTimezoneRef = useRef(selectedTimezone);

  const isAmbassadorRescheduleRequested =
    checkAndGetInvitationStatus(projectDetails) ===
    ENUM_PROJECT_STATUS.AMBASSADOR_RESCHEDULE_REQUESTED;

  const [slotMode, setSlotMode] = useState<"select" | "suggest">(
    isAmbassadorRescheduleRequested ? "suggest" : "select"
  );

  useEffect(() => {
    if (isAmbassadorRescheduleRequested && slotMode !== "suggest") {
      setSlotMode("suggest");
    }
  }, [isAmbassadorRescheduleRequested, slotMode]);

  const shouldShowHelpButton =
    projectDetails?.availableTimeSlots?.length > 0 &&
    projectDetails?.suggestedTimeSlots?.length > 0;

  const ambassadorTimezoneValue =
    projectDetails?.ambassador?.timezone?.value ||
    timezone ||
    selectedTimezoneVal;
  const clientTimezoneValue = projectDetails?.client?.timezone?.value;

  const ambassadorTimezoneAbbr = useMemo(() => {
    return ambassadorTimezoneValue
      ? moment().tz(ambassadorTimezoneValue).format("z")
      : undefined;
  }, [ambassadorTimezoneValue]);

  const clientTimezoneAbbr = useMemo(() => {
    return clientTimezoneValue
      ? moment().tz(clientTimezoneValue).format("z")
      : undefined;
  }, [clientTimezoneValue]);

  const availableTimeArr = useMemo(() => {
    if (!selectedDate || !Array.isArray(availableDatesArr)) {
      return [];
    }
    const timeSlots = getUserAvailableTimeSlots({
      availableDatesArr,
      selectedDate,
    });
    return Array.isArray(timeSlots) ? timeSlots : [];
  }, [selectedDate, availableDatesArr]);


  const { slotsByDate, pastSlotsByDate } = useMemo(() => {
    if (isCalendly) {
      return { slotsByDate: {}, pastSlotsByDate: {} };
    }

    return {
      slotsByDate: groupAvailableTimesByDate(availableDatesArr, false),
      pastSlotsByDate: groupAvailableTimesByDate(availableDatesArr, true),
    };
  }, [isCalendly, availableDatesArr]);

  const CALENDLY_MONTH_LOOKAHEAD = 3;

  const parseCalendlyResponseDates = (responseData: any[]): Date[] => {
    const dates: Date[] = [];
    responseData?.forEach((d) => {
      if (d?.slots?.length) {
        d.slots.forEach((t) => {
          dates.push(moment(d.date + " " + t).toDate());
        });
      }
    });
    return dates.filter((date) => moment(date).isAfter(moment()));
  };

  const getMonthConfigForDate = (date: Date) =>
    getStartAndEndOfMonth({
      date,
      config: {
        format: "MM-DD-YYYY",
        minDate: new Date(),
      },
    });

  const fetchCalendlySlotsForMonth = async (monthConfig: {
    startOfMonth: string;
    endOfMonth: string;
  }) => {
    const response = await dispatch(
      getVendorCalendlySlots({
        id: invitationId,
        start: monthConfig.startOfMonth,
        end: monthConfig.endOfMonth,
        timezone: selectedTimezone,
      })
    );

    if (!response?.success) {
      return [];
    }

    return parseCalendlyResponseDates(response?.data || []);
  };

  const runInitialCalendlyScan = async () => {
    setLoading(true);
    setNoCalendlySlotsFound(false);

    let monthDate = value ? new Date(value) : new Date();

    for (let attempt = 0; attempt < CALENDLY_MONTH_LOOKAHEAD; attempt++) {
      const monthConfig = getMonthConfigForDate(monthDate);
      const dates = await fetchCalendlySlotsForMonth(monthConfig);

      if (dates.length > 0) {
        skipNextCalendlyMonthFetchRef.current = true;
        setSelectedMonth(monthConfig);
        setAvailableDatesArr(dates);
        setNoCalendlySlotsFound(false);
        setLoading(false);
        return;
      }

      monthDate = moment(monthDate).add(1, "month").startOf("month").toDate();
    }

    setAvailableDatesArr([]);
    setNoCalendlySlotsFound(true);
    setLoading(false);
  };

  const onCalenderDateChange = async () => {
    setLoading(true);
    setNoCalendlySlotsFound(false);

    const dates = await fetchCalendlySlotsForMonth(selectedMonth);
    setAvailableDatesArr(dates);
    setLoading(false);
  };

  const fetchBlockedDatesForMonth = useCallback(
    async (monthDate: Date) => {
      setLoadingBlockedDates(true);
      try {
        const monthMoment = moment(monthDate);
        const from = monthMoment.startOf("month").format("YYYY-MM-DD");
        const to = monthMoment.endOf("month").format("YYYY-MM-DD");

        const response = await dispatch(getBlockedDates({ from, to }));

        if (response?.success && response?.data) {
          setBlockedDates(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch blocked dates:", error);
      } finally {
        setLoadingBlockedDates(false);
      }
    },
    [dispatch]
  );

  const handleSlotModalCalendarMonthChange = useCallback(
    (monthStart: Dayjs) => {
      fetchBlockedDatesForMonth(monthStart.toDate());
    },
    [fetchBlockedDatesForMonth]
  );

  useEffect(() => {
    if (!isCalendly) {
      return;
    }

    const timezoneChanged =
      prevCalendlyTimezoneRef.current !== selectedTimezone;
    prevCalendlyTimezoneRef.current = selectedTimezone;

    if (timezoneChanged && calendlyInitialScanDoneRef.current) {
      calendlyInitialScanDoneRef.current = false;
    }

    if (!calendlyInitialScanDoneRef.current) {
      calendlyInitialScanDoneRef.current = true;
      runInitialCalendlyScan();
      return;
    }

    if (skipNextCalendlyMonthFetchRef.current) {
      skipNextCalendlyMonthFetchRef.current = false;
      return;
    }

    onCalenderDateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedTimezone, isCalendly]);

  useEffect(() => {
    const monthDate = selectedMonth?.startOfMonth
      ? moment(selectedMonth.startOfMonth, "MM-DD-YYYY").toDate()
      : new Date();
    fetchBlockedDatesForMonth(monthDate);
  }, [selectedMonth, fetchBlockedDatesForMonth]);

  const onTimeSelect = (time: Date) => {
    setSelectedSlot(time);
    setIsTimeConfirmed(true);
  };

  const onDateSelect = (date: any) => {
    setSelectedDate(new Date(date));
    setIsTimeConfirmed(false);
    setSelectedSlot(null);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedTimezone(e.target.value);
    setIsTimeConfirmed(false);
    setSelectedSlot(null);
  };

  const onMonthChange = (date: any) => {
    const nextMonth = getMonthConfigForDate(new Date(date));

    if (nextMonth.startOfMonth === selectedMonth?.startOfMonth) {
      return;
    }

    setSelectedDate(null);
    setIsTimeConfirmed(false);
    setSelectedSlot(null);
    setSelectedMonth(nextMonth);
  };


  const validateAmbassadorSlots = (item: { slots: VendorSlot[] }) => {
    let isFormValid = true;
    let slotSelectionCount = 0;

    const isAllSlotsEmpty = item.slots.every(
      (itemslot) =>
        !itemslot.selectedSlotTimeFrom && !itemslot.selectedSlotTimeTo
    );

    if (isAllSlotsEmpty) {
      return {
        updatedList: item,
        isFormValid: false,
        formMessage: "Please select the times",
      };
    }

    item.slots.forEach((slotsItem, slotsIndex) => {
      const overLapIndex: number[] = [];

      if (slotsItem.selectedSlotTimeFrom && slotsItem.selectedSlotTimeTo) {
        slotSelectionCount++;
      }

      item.slots.forEach((slotsItemInner, slotsIndexInner) => {
        if (
          slotsIndexInner !== slotsIndex &&
          slotsItem.selectedSlotTimeFrom &&
          slotsItem.selectedSlotTimeTo &&
          slotsItemInner.selectedSlotTimeFrom &&
          slotsItemInner.selectedSlotTimeTo &&
          moment(slotsItem.selectedSlotTimeFrom).diff(
            moment(slotsItemInner.selectedSlotTimeFrom),
            "minutes"
          ) === 0 &&
          moment(slotsItem.selectedSlotTimeTo).diff(
            moment(slotsItemInner.selectedSlotTimeTo),
            "minutes"
          ) === 0
        ) {
          overLapIndex.push(slotsIndexInner + 1);
        }
      });

      if (overLapIndex.length > 0) {
        slotsItem.error = `Matching with slot ${overLapIndex.join(",")}`;
        isFormValid = false;
      } else if (
        (slotsItem.selectedSlotTimeTo && !slotsItem.selectedSlotTimeFrom) ||
        (!slotsItem.selectedSlotTimeTo && slotsItem.selectedSlotTimeFrom)
      ) {
        slotsItem.error = "From and To date are required";
        isFormValid = false;
      } else {
        slotsItem.error = "";
      }
    });

    const isSlotsValid = item.slots.every((slot) => !slot.error);

    if (slotSelectionCount < 3) {
      return {
        updatedList: item,
        isFormValid: false,
        formMessage: "Please select at least 3 alternative times",
      };
    }

    return {
      updatedList: item,
      isFormValid: isSlotsValid,
      formMessage: isSlotsValid
        ? ""
        : "Please check all the selected times",
    };
  };

  const buildCustomSlot = (
    from: Date,
    to: Date,
    isTimeRange: boolean
  ): VendorSlot => {
    return {
      selectedSlotTimeFrom: from,
      selectedSlotTimeTo: to,
      isTimeRange,
      error: "",
      convertedSlotTimeFrom: from?.toISOString(),
      convertedSlotTimeTo: to?.toISOString(),
    };
  };

  const commitCustomSlot = (slot: VendorSlot, modalState: SlotModalState) => {
    const listClone = _.cloneDeep(customSlotState);

    if (modalState?.mode === "edit" && modalState.index !== null) {
      listClone.slots[modalState.index] = slot;
    } else {
      listClone.slots.push(slot);
    }

    if (manualSubmitClicked) {
      const validation = validateAmbassadorSlots(listClone);
      setCustomSlotState(validation.updatedList);
      setManualFormError(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setCustomSlotState(listClone);
      setManualFormError("");
    }
  };

  const commitCustomSlots = (slots: VendorSlot[]) => {
    if (slots.length === 0) return;
    const listClone = _.cloneDeep(customSlotState);
    listClone.slots = [...(listClone.slots || []), ...slots];
    if (manualSubmitClicked) {
      const validation = validateAmbassadorSlots(listClone);
      setCustomSlotState(validation.updatedList);
      setManualFormError(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setCustomSlotState(listClone);
      setManualFormError("");
    }
  };

  const handleCustomSlotAdd = () => {
    setIsUpdatingAvailability(customSlotState.slots.length > 0);
    setCustomSlotModalState({ mode: "create", index: null });
  };

  const handleCustomSlotEdit = (index: number) => {
    setCustomSlotModalState({ mode: "edit", index });
  };

  const handleCustomSlotRemove = (index: number) => {
    const listClone = _.cloneDeep(customSlotState);
    listClone.slots.splice(index, 1);

    if (manualSubmitClicked) {
      const validation = validateAmbassadorSlots(listClone);
      setCustomSlotState(validation.updatedList);
      setManualFormError(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setCustomSlotState(listClone);
      setManualFormError("");
    }
  };

  const handleCustomSlotSubmit = (data) => {
    if (!customSlotModalState) return;

      const payloads = Array.isArray(data) ? data : [data];
    const slots = [];

      for (const p of payloads) {
        const { dateFrom, dateTo } = getFromAndToDateTimeRange(p);
        const slot = buildCustomSlot(
          dateFrom,
          dateTo,
          p?.type === "datetimerange"
        );

        slots.push(slot);
    }

    if (isUpdatingAvailability) {
      const nextState = { slots };
      if (manualSubmitClicked) {
        const validation = validateAmbassadorSlots(nextState);
        setCustomSlotState(validation.updatedList);
        setManualFormError(validation.isFormValid ? "" : validation.formMessage);
      } else {
        setCustomSlotState(nextState);
        setManualFormError("");
      }
      setCustomSlotModalState(null);
      setIsUpdatingAvailability(false);
      return;
    }

    if (slots.length === 1) {
      commitCustomSlot(slots[0], customSlotModalState);
    } else {
      commitCustomSlots(slots);
    }
    setCustomSlotModalState(null);
    setIsUpdatingAvailability(false);
  };

  const handleSlotSelectionSubmit = async () => {
    setSubmitting(true);

    const vendorSlot = projectDetails?.availableTimeSlots?.find(
      (f) => f.from === selectedSlot
    );

    const payload = {
      selectedTimeSlot: vendorSlot || {
        from: moment(selectedSlot).toISOString(),
        to: moment(selectedSlot).add(45, "minutes").toISOString(),
      },
      isCustom: false,
    };

    const response = await dispatch(
      submitAmbassadorSelectedSlot(payload, invitationId)
    );

    setSubmitting(false);

    if (response?.success) {
      if (onSuccess) {
        await onSuccess();
        return;
      }
      if (lastLocation?.pathname?.includes("/project/invitation")) {
        return navigate(ROUTE_PATHS.PROJECT.LIST);
      }
      navigate(-1);
    }
  };

  const onRequestCoordinationSuccess = () => {
    navigate(ROUTE_PATHS.PROJECT.LIST);
  };

  const onSubmit = async () => {
    if (slotMode === "select") {
      handleSlotSelectionSubmit();
      return;
    }

    setManualSubmitClicked(true);
    const validation = validateAmbassadorSlots(customSlotState);
    setCustomSlotState(validation.updatedList);

    if (!validation.isFormValid) {
      setManualFormError(validation.formMessage);
      return;
    }

    setManualFormError("");
    setSubmitting(true);

    const payload = {
      suggestedTimeSlots: buildSuggestedTimeSlotsPayload(
        validation.updatedList.slots,
        clientTimezoneValue
      ),
    };

    const response = await dispatch(
      submitAmbassadorSuggestedSlot(payload, invitationId)
    );

    setSubmitting(false);

    if (response?.success) {
      if (onSuccess) {
        await onSuccess();
        return;
      }
      if (lastLocation?.pathname?.includes("/project/invitation")) {
        return navigate(ROUTE_PATHS.PROJECT.LIST);
      }
      navigate(-1);
    }
  };

  const calendarSelectedDate = selectedDate
    ? dayjs(moment(selectedDate).format("YYYY-MM-DD"))
    : null;
  const calendarReferenceDate = selectedMonth?.startOfMonth
    ? dayjs(moment(selectedMonth.startOfMonth, "MM-DD-YYYY").format("YYYY-MM-DD"))
    : dayjs();

  return (
    <>
      <Stack flex={1} height="100%">
        <Box
          flex={1}
          overflow="auto"
        >
          <Box
          >
            <Box>
              <AppAlert severity="info" sx={{ fontSize: "small" }}>
                If the vendor times below are not feasible for you, you can
                suggest alternative times by clicking the "Suggest time" button below.
              </AppAlert>
            </Box>

            {!isAmbassadorRescheduleRequested && (
              <RadioGroup
                row
                value={slotMode}
                onChange={(_, value) => setSlotMode(value as "select" | "suggest")}
                sx={{ my: 2 }}
              >
                <FormControlLabel
                  value="select"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body-ibmPlexMono-medium">
                      Select time
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="suggest"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body-ibmPlexMono-medium">
                      Suggest time
                    </Typography>
                  }
                />
              </RadioGroup>
            )}

            {isCalendly && slotMode === "select" && (
              <Box my={1}>
                <BrowserTimezoneAlert />
              </Box>
            )}

            {isCalendly && slotMode === "select" && noCalendlySlotsFound && (
              <Box my={1}>
                <AppAlert severity="warning">
                  No slots available for the next 3 months. Please suggest
                  alternative times or{" "}
                  <Link
                    href={`mailto:${process.env.REACT_APP_SECONDARY_SUPPORT_TEAM_EMAIL_ID}`}
                    underline="none"
                    color="inherit"
                    sx={{ fontWeight: "inherit" }}
                  >
                    <Typography
                      component="span"
                      variant="body-ibmPlexMono-bold"
                      color={APP_COLORS.secondaryDark}
                    >
                      contact us
                    </Typography>
                  </Link>
                  .
                </AppAlert>
              </Box>
            )}

            {slotMode === "select" && (isCalendly ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <LoadingSpinner
                    color="grey"
                    spinnerType="PulseLoader"
                    loading={loading}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      key={selectedMonth?.startOfMonth}
                      onChange={onDateSelect}
                      sx={{ m: 0 }}
                      onMonthChange={onMonthChange}
                      loading={loading}
                      value={calendarSelectedDate}
                      referenceDate={calendarReferenceDate}
                      renderLoading={() => <DayCalendarSkeleton />}
                      disablePast
                      shouldDisableDate={(date) => {
                        const isNotAvailable = !availableDatesArr?.some((d) =>
                          isSameDate(date, d)
                        );
                        if (isNotAvailable) return true;
                        const dateStr = moment(date["$d"] || date).format(
                          "YYYY-MM-DD"
                        );
                        if (blockedDates.includes(dateStr)) return true;
                        return false;
                      }}
                      slots={{ day: ServerDay }}
                      slotProps={{
                        day: {
                          highlightedDays: availableDatesArr,
                          blockedDates: blockedDates,
                          holidayDates: holidayDates,
                          theme: theme,
                        } as any,
                      }}
                    />
                    <Stack gap={0.5} mt={0.75}>
                      {blockedDates && blockedDates.length > 0 && (
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "2px",
                              backgroundColor: alpha(
                                theme.palette.error.main,
                                0.25
                              ),
                              border: `1px solid ${theme.palette.error.main}`,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.error.main,
                              fontSize: "0.7rem",
                              lineHeight: 1.2,
                            }}
                          >
                            Slots are already full for dates marked in red
                          </Typography>
                        </Box>
                      )}
                      {holidayDates.length > 0 && (
                        <Box
                          sx={{
                            px: 0.75,
                            py: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "2px",
                              backgroundColor: alpha(
                                theme.palette.warning.main,
                                0.25
                              ),
                              border: `1px solid ${theme.palette.warning.main}`,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.warning.dark,
                              fontSize: "0.7rem",
                              lineHeight: 1.2,
                            }}
                          >
                            Holidays
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </LocalizationProvider>
                  <Box mt={2}>
                    <InputLabel required={true} htmlFor={"eventType"} shrink>
                      Time zone
                    </InputLabel>
                    <AppAutoSelect
                      options={data}
                      inputProps={{
                        id: "eventType",
                        value: selectedTimezone,
                        name: "eventType",
                        variant: "outlined",
                        onChange: handleOnChange,
                        disabled: !isCalendly,
                      }}
                      autoSelecboxListConfig={{
                        labelField: "text",
                        valueField: "value",
                      }}
                      labelProps={{ label: "" }}
                      rootProps={{ margin: "none" }}
                      loading={timezoneLoading}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box
                    sx={{
                      borderRadius: 1,
                      backgroundColor: "background.paper",
                      py: 3,
                      minHeight: 300,
                    }}
                  >
                    {Array.isArray(availableTimeArr) &&
                      availableTimeArr.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {availableTimeArr.map((t) => {
                          const timeKey = moment(t).format(
                            "YYYY-MM-DDTHH:mm:ss"
                          );
                          const isSelected =
                            selectedSlot &&
                            moment(selectedSlot).format("HH:mm") ===
                            moment(t).format("HH:mm");
                          const isConfirmed = isSelected && isTimeConfirmed;
                          return (
                            <Chip
                              key={timeKey}
                              label={moment(t).format("hh:mm A")}
                              onClick={() => onTimeSelect(t)}
                              color={isConfirmed ? "primary" : "default"}
                              variant={isConfirmed ? "filled" : "outlined"}
                              sx={{
                                fontWeight: isConfirmed ? 600 : 500,
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: isConfirmed
                                    ? "primary.dark"
                                    : "action.hover",
                                },
                                borderRadius: 1,
                              }}
                            />
                          );
                        })}
                      </Stack>
                    ) : selectedDate ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 4,
                          minHeight: 250,
                        }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.1
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <AccessTimeOutlinedIcon
                            sx={{
                              fontSize: 32,
                              color: theme.palette.warning.main,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          color="text.primary"
                          mb={0.5}
                        >
                          No times available
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ maxWidth: 300 }}
                        >
                          There are no available times for the selected
                          date. Please select a different date or provide your
                          availability below.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 250,
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{
                              fontSize: 24,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.875rem",
                            textAlign: "center",
                          }}
                        >
                          Please select a date to see available times
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Stack gap={1}>
                <BrowserTimezoneAlert />
                <DateWiseSlotPicker
                  slotsByDate={slotsByDate}
                  pastSlotsByDate={pastSlotsByDate}
                  selectedSlot={selectedSlot}
                  isTimeConfirmed={isTimeConfirmed}
                  onSlotSelect={(t) => {
                    setSelectedSlot(t);
                    setSelectedDate(new Date(t));
                    setIsTimeConfirmed(true);
                  }}
                  onSwitchToSuggestTime={() => setSlotMode("suggest")}
                />
              </Stack>
            ))}

            {slotMode === "suggest" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {manualFormError && (
                    <Box mb={3}>
                      <FormHelperTextError showIcon error={manualFormError} />
                    </Box>
                  )}

                  <AmbassadorManualSlotsSection
                    slots={customSlotState.slots}
                    browserTimezone={browserTimezone}
                    browserTimezoneAbbr={moment
                      .tz(new Date(), browserTimezone)
                      .format("z")}
                    clientTimezoneAbbr={clientTimezoneAbbr}
                    clientTimezoneValue={clientTimezoneValue}
                    onAddSlot={handleCustomSlotAdd}
                    onEditSlot={handleCustomSlotEdit}
                    onRemoveSlot={handleCustomSlotRemove}
                    showErrors={manualSubmitClicked}
                  />
                </Grid>
              </Grid>
            )}
          </Box>

        </Box>

        <FlexBox
          justifyContent="flex-end"
          position="sticky"
          bottom={0}
          zIndex={10}
          bgcolor="background.paper"
          borderTop={1}
          borderColor="divider"
          px={2}
          pt={2}
        >
          <FlexBox gap={1}>
            {shouldShowHelpButton && (
              <RequestCoordinationButton
                invitationId={invitationId}
                initiatedBy="ambassador"
                size="small"
                variant="outlined"
                color="secondary"
                onSuccess={onRequestCoordinationSuccess}
              />
            )}
            <DefaultButton onClick={onCancel} sx={{ width: { sm: 180 } }}>
              Cancel
            </DefaultButton>
            <PrimaryButton
              onClick={onSubmit}
              disabled={
                submitting ||
                (slotMode === "suggest"
                  ? !customSlotState.slots.length
                  : !selectedDate || !isTimeConfirmed)
              }
              loading={submitting}
              sx={{ width: { sm: 220 } }}
            >
              Submit
            </PrimaryButton>
          </FlexBox>
        </FlexBox>
      </Stack>

      {customSlotModalState && (
        <SlotSelectionModal
          onClose={() => {
            setCustomSlotModalState(null);
            setIsUpdatingAvailability(false);
          }}
          onSubmit={handleCustomSlotSubmit}
          blockedDates={blockedDates}
          onCalendarMonthChange={handleSlotModalCalendarMonthChange}
          initialSelectedTimes={customSlotState.slots.map((s) => s.selectedSlotTimeFrom).filter(Boolean)}
          slotIndex={
            customSlotModalState.mode === "edit" &&
              customSlotModalState.index !== null
              ? customSlotModalState.index + 1
              : customSlotState.slots.length + 1
          }
          slotDetails={
            customSlotModalState.mode === "edit" &&
              customSlotModalState.index !== null
              ? customSlotState.slots[customSlotModalState.index]
              : null
          }
          oppositeUserTimezone={clientTimezoneValue}
          userRole="vendor"
          allowMultiSelect={
            customSlotModalState.mode === "create" &&
            customSlotModalState.index === null
          }
          totalSlots={customSlotState.slots.length}
        />
      )}
    </>
  );
}

function ServerDay(props: any) {
  const {
    highlightedDays = [],
    day,
    outsideCurrentMonth,
    blockedDates = [],
    holidayDates = [],
    theme,
    ...other
  } = props;

  const isSelected =
    !outsideCurrentMonth &&
    highlightedDays?.some((d: Date) =>
      isSameDate(moment(d), moment(new Date(day)))
    );

  const dateStr = moment(day["$d"] || day).format("YYYY-MM-DD");
  const isBlocked = blockedDates.includes(dateStr);
  const isHoliday = holidayDates.includes(dateStr);

  return (
    <PickersDay
      {...other}
      outsideCurrentMonth={outsideCurrentMonth}
      day={day}
      sx={{
        fontWeight: 700,
        ...(isHoliday &&
          !isBlocked && {
            backgroundColor: theme
              ? alpha(theme.palette.warning.main, 0.18)
              : alpha("#ed6c02", 0.18),
            color: theme ? theme.palette.warning.dark : "#e65100",
            border: theme
              ? `1px solid ${alpha(theme.palette.warning.main, 0.4)}`
              : `1px solid ${alpha("#ed6c02", 0.4)}`,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: theme
                ? alpha(theme.palette.warning.main, 0.28)
                : alpha("#ed6c02", 0.28),
            },
          }),
        ...(isSelected && {
          backgroundColor: "#eef5ff",
          color: "#0060e6",
        }),
        ...(isBlocked && {
          backgroundColor: theme
            ? alpha(theme.palette.error.main, 0.25)
            : alpha("#f44336", 0.25),
          color: theme ? theme.palette.error.main : "#f44336",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: theme
              ? alpha(theme.palette.error.main, 0.3)
              : alpha("#f44336", 0.3),
          },
        }),
      }}
    />
  );
}
